import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import { StarRatingInput } from '../components/StarRating';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReviewPage() {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [canReview, setCanReview] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Form state
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [clientName, setClientName] = useState('');
  
  // Provider info from URL params
  const providerId = searchParams.get('providerId');
  const providerName = searchParams.get('providerName');
  const providerSlug = searchParams.get('providerSlug');

  useEffect(() => {
    checkCanReview();
  }, [bookingId]);

  const checkCanReview = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/bookings/${bookingId}/can-review`);
      setCanReview(res.data.canReview);
      setReason(res.data.reason);
    } catch (error) {
      console.error('Error checking review eligibility:', error);
      setCanReview(false);
      setReason('Unable to verify booking');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    if (!comment.trim()) {
      toast.error('Please write a comment');
      return;
    }
    
    if (!clientName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    try {
      setSubmitting(true);
      
      await axios.post(`${API_URL}/api/reviews`, {
        providerId,
        bookingId,
        clientName: clientName.trim(),
        rating,
        comment: comment.trim()
      }, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
      
      toast.success('Thank you for your review!');
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-green-100 rounded-full p-4">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-2xl">Review Submitted!</CardTitle>
            <CardDescription className="text-base mt-2">
              Thank you for sharing your experience with {providerName || 'this provider'}.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={() => navigate(providerSlug ? `/providers/${providerSlug}` : '/')}
              className="w-full"
            >
              {providerSlug ? 'Back to Provider' : 'Back to Home'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!canReview) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Unable to Review</CardTitle>
            <CardDescription className="text-base mt-2">
              {reason === 'Booking not completed' 
                ? 'This booking has not been completed yet. You can leave a review after your service is complete.'
                : reason === 'Already reviewed'
                ? 'You have already submitted a review for this booking.'
                : reason || 'Unable to process your review request.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="w-full"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="bg-card border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Logo variant="compact" size="sm" />
          </div>
        </div>
      </nav>

      <div className="max-w-xl mx-auto px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Leave a Review</CardTitle>
            <CardDescription>
              Share your experience with {providerName || 'this provider'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Your Name */}
              <div className="space-y-2">
                <Label htmlFor="clientName">Your Name *</Label>
                <Input
                  id="clientName"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Enter your name"
                  required
                  data-testid="review-name-input"
                />
              </div>

              {/* Star Rating */}
              <div className="space-y-2">
                <Label>Your Rating *</Label>
                <div className="flex items-center gap-2">
                  <StarRatingInput 
                    rating={rating} 
                    onChange={setRating}
                    size="lg"
                  />
                  {rating > 0 && (
                    <span className="text-lg font-semibold ml-2">{rating}/5</span>
                  )}
                </div>
              </div>

              {/* Comment */}
              <div className="space-y-2">
                <Label htmlFor="comment">Your Review *</Label>
                <Textarea
                  id="comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Tell others about your experience..."
                  rows={4}
                  required
                  data-testid="review-comment-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={submitting || rating === 0}
                data-testid="submit-review-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Review'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
